import * as React from 'react';
import { z } from 'zod';
import { Part } from 'api/domain/entities/documents/PostWorkQuestionnaire';
import { FormikProvider, useFormik } from 'formik';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import { useEffect, useState } from 'react';

import { useTranslationPrefix } from 'infrastructure/translations/i18n';
import { Modal } from 'targets/web/components';
import { FormikTextField } from 'targets/web/modules/dashboard/components';

interface Props {
  part?: Part[];
  onClose: () => void;
  onSave: (name: Part[], performedBy: string) => void;
}

const PerformedByModal: React.FC<Props> = ({ part, onClose, onSave }) => {
  const t = useTranslationPrefix('jobs.post_work_questionnaire');

  const [input, setInput] = useState<HTMLInputElement | null>(null);

  const formik = useFormik({
    initialValues: {
      performedBy: '',
    },
    validationSchema: toFormikValidationSchema(
      z.object({
        performedBy: z.string().max(30).min(1),
      }),
    ),
    validateOnBlur: false,
    onSubmit: ({ performedBy }) => {
      if (!part?.length) {
        return;
      }
      onSave(part, performedBy);
      formik.resetForm();
    },
  });

  useEffect(() => {
    if (input) {
      input.focus();
    }
  }, [input]);

  return (
    <FormikProvider value={formik}>
      <Modal
        data-testname="performedByModal"
        open={!!part?.length}
        onClose={onClose}
        disableBackdropClick
        maxWidth="sm"
        fullWidth
        actions={{
          primary: {
            disabled: !(formik.isValid && formik.dirty),
            text: 'Save',
            onClick: () => {
              if (!part?.length) {
                return;
              }
              formik.handleSubmit();
            },
          },
          secondary: {
            text: 'Cancel',
            onClick: () => {
              formik.resetForm();
              onClose();
            },
          },
        }}
        title={t('performed_by_title')}
        content={
          <FormikTextField
            inputRef={setInput}
            resetOnUnmount={false}
            name="performedBy"
            label={t('performed_by_label')}
          />
        }
      />
    </FormikProvider>
  );
};

export default PerformedByModal;
